/* ============================================================================
   APERÇU DE LA LANDING — « Couleur / Trait / Diaporama »
   ----------------------------------------------------------------------------
   Le hero de la page d'accueil montre une VRAIE situation rendue par le moteur
   (pas une image figée) : le visiteur bascule entre les trois rendus de TactiQ.
     - Couleur (défaut) / Trait : le SCHÉMA COMPLET (tous les temps visibles),
       rendu par renderToCanvas en mode "color" / "line" ;
     - Diaporama : la même scène DÉROULÉE temps par temps, en boucle, avec
       l'animation de projection (les actions du temps courant glissent) — le
       même découpage que le mode projection de l'app.
   Autonome : s'auto-initialise à l'import (via main.js). Sans le canevas du
   hero dans le DOM, il ne fait rien (pages sans landing). L'animation ne tourne
   QUE quand « Diaporama » est choisi ET l'onglet visible (économie de CPU) ;
   « réduire les animations » (préférence système) affiche la scène complète.
   ========================================================================== */
import { renderToCanvas } from "./render.js";
import { buildProjectionSteps, sceneForStepAnimated } from "./projection.js";

/* Contre-attaque 3 contre 2 (football) — scène DÉTERMINISTE figée par le moteur
   (localParse), curée pour la démo : les replis défenseurs portent order 2 afin
   que le diaporama finisse pile sur la frappe (5 temps). Voir la genèse dans
   l'historique de conversation / docs. */
const SCENE = {
  kind: "match", sport: "football", title: "Contre-attaque à 3 contre 2",
  pedagogy: { objectif: "Contre-attaque en football à 3 contre 2 : trois attaquants lancés face à deux défenseurs et au gardien", organisation: [], criteres_reussite: [], erreurs: [] },
  zones: [],
  markers: [{ type: "ballon", x: 31, y: 50 }],
  players: [
    { id: "A1", team: "A", color: "#0072B2", x: 20, y: 22, role: "Attaquant" },
    { id: "A2", team: "A", color: "#0072B2", x: 26, y: 50, role: "Porteur" },
    { id: "A3", team: "A", color: "#0072B2", x: 32, y: 78, role: "Ailier droit" },
    { id: "D1", team: "B", color: "#D55E00", x: 44, y: 38, role: "Défenseur 1" },
    { id: "D2", team: "B", color: "#D55E00", x: 44, y: 62, role: "Défenseur 2" },
    { id: "G", team: "k", color: "#F2C200", x: 94, y: 50, role: "Gardien" },
  ],
  actions: [
    { from: "A2", tox: 46, toy: 50, type: "dribble", style: "solid", order: 1 },
    { fromx: 46, fromy: 50, to: "A3", type: "passe", style: "solid", order: 2 },
    { from: "A3", tox: 86, toy: 72, type: "dribble", order: 3 },
    { fromx: 86, fromy: 72, tox: 97, toy: 50, type: "tir", style: "solid", order: 4 },
    { from: "D1", tox: 50, toy: 55, type: "course", style: "dashed", order: 2 },
    { from: "D2", tox: 50, toy: 67, type: "course", style: "dashed", order: 2 },
  ],
  meta: { renderMode: "color" },
};

const SPORT = "football";
const ANIM = 750;      // durée d'un temps animé (aligné sur projection.js)
const HOLD = 620;      // pause après chaque temps
const HOLD_FIRST = 850;   // pause sur la mise en place
const HOLD_LAST = 1700;   // pause finale (sur la frappe) avant de reboucler

export function initHeroPreview() {
  const canvas = document.getElementById("lpPreviewCanvas");
  const seg = document.getElementById("lpPreviewSeg");
  if (!canvas || !seg) return;   // pas de hero sur cette page

  // Géométrie de référence VOLONTAIREMENT AGRANDIE (≈ ×1.3 vs l'app) : les
  // pastilles / flèches / étiquettes sont dessinées à taille FIXE dans cet
  // espace logique. Un espace plus grand (même place à l'écran via width:100%)
  // rend donc les éléments proportionnellement PLUS PETITS et le terrain plus
  // lisible — réglage propre à l'aperçu de la landing.
  // Résolution interne = géométrie × densité (net sur écrans HiDPI).
  const BASE_W = 546, BASE_H = 351;
  const scale = Math.min(3, 2 * (window.devicePixelRatio || 1));
  canvas.width = Math.round(BASE_W * scale);
  canvas.height = Math.round(BASE_H * scale);

  const steps = buildProjectionSteps(SCENE);
  const reduced = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Programme du diaporama : mise en place, puis chaque temps (glissé + pause).
  const schedule = [{ step: 0, anim: false, dur: HOLD_FIRST }];
  for (let i = 1; i < steps.length; i++) {
    schedule.push({ step: i, anim: true, dur: ANIM });
    schedule.push({ step: i, anim: false, dur: i === steps.length - 1 ? HOLD_LAST : HOLD });
  }
  const cycle = schedule.reduce((s, seg) => s + seg.dur, 0);

  let mode = "color";     // "color" | "line" | "slides"
  let raf = 0, t0 = 0;

  const drawStatic = (renderMode) => {
    // Schéma COMPLET = la scène BRUTE (toutes les actions en pleine couleur,
    // comme dans l'éditeur). On ne passe PAS par sceneForStep, qui atténue les
    // temps passés (affordance réservée au diaporama) et délavait les flèches.
    renderToCanvas(canvas, SCENE, SPORT, renderMode, { compact: true, scale });
  };

  const drawAt = (elapsed) => {
    let e = elapsed % cycle;
    for (const seg of schedule) {
      if (e < seg.dur) {
        const t = seg.anim ? Math.min(1, e / seg.dur) : 1;
        // easeOutCubic : rapide puis amorti (même ressenti que la projection).
        const eased = seg.anim ? 1 - Math.pow(1 - t, 3) : 1;
        const view = sceneForStepAnimated(SCENE, steps, seg.step, eased);
        renderToCanvas(canvas, view, SPORT, "color", { compact: true, scale });
        return;
      }
      e -= seg.dur;
    }
  };

  const stopLoop = () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } };
  const tick = (now) => {
    if (mode !== "slides") { raf = 0; return; }
    drawAt(now - t0);
    raf = requestAnimationFrame(tick);
  };
  const startLoop = () => {
    stopLoop();
    if (reduced()) { drawStatic("color"); return; }   // pas d'animation : scène complète
    t0 = performance.now();
    raf = requestAnimationFrame(tick);
  };

  const apply = (m) => {
    mode = m;
    seg.querySelectorAll("button").forEach((b) =>
      b.classList.toggle("is-active", b.getAttribute("data-preview") === m));
    if (m === "slides") startLoop();
    else { stopLoop(); drawStatic(m === "line" ? "line" : "color"); }
  };

  seg.querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => apply(b.getAttribute("data-preview"))));

  // Onglet caché : on gèle la boucle (reprise à la ré-affichage si Diaporama actif).
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stopLoop();
    else if (mode === "slides") startLoop();
  });

  apply("color");   // défaut : Couleur, statique
}

// Auto-init (le canevas du hero est déjà parsé quand ce module s'exécute).
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initHeroPreview);
else initHeroPreview();
